/**
 * TierGuard – Feature gating wrapper op basis van subscription tier.
 * Als de tenant het vereiste tier niet heeft, toont een "Upgrade vereist" locked state.
 */

import React from 'react'
import { Lock, Crown } from 'lucide-react'
import { useTenantTier, type TierName } from '../../hooks/useTenantTier'

interface TierGuardProps {
  children: React.ReactNode
  requiredTier: TierName
}

export default function TierGuard({ children, requiredTier }: TierGuardProps) {
  const { hasTier, isLoading } = useTenantTier()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-pulse text-gray-500 dark:text-gray-400">Laden...</div>
      </div>
    )
  }

  if (hasTier(requiredTier)) {
    return <>{children}</>
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[280px] px-6 py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-600">
      <div className="flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-amber-100 dark:bg-amber-900/30">
        <Crown className="w-8 h-8 text-amber-600 dark:text-amber-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        Upgrade vereist
      </h3>
      <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-6">
        Upgrade uw abonnement om toegang te krijgen tot deze geavanceerde module.
      </p>
      <a
        href="/billing"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
      >
        <Lock className="w-4 h-4" />
        Bekijk Abonnementen
      </a>
    </div>
  )
}
